import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Fullscreen product image lightbox with click-to-zoom, gallery navigation,
 * keyboard shortcuts (arrows / Esc) and mobile swipe. Dependency-free.
 */
const ImageLightbox = ({ images, index, onIndexChange, onClose, alt }) => {
  const [zoom, setZoom] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const touchStartX = useRef(null);
  const total = images.length;

  const go = useCallback(
    (dir) => {
      setZoom(false);
      onIndexChange((index + dir + total) % total);
    },
    [index, total, onIndexChange],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, onClose]);

  const handleMove = (e) => {
    if (!zoom) return;
    const r = e.currentTarget.getBoundingClientRect();
    setOrigin({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || zoom) { touchStartX.current = null; return; }
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col" role="dialog" aria-modal="true" data-testid="image-lightbox">
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 text-white">
        <span className="font-display tracking-[0.2em] text-sm text-neutral-300">{index + 1} / {total}</span>
        <button onClick={onClose} aria-label="Close" data-testid="lightbox-close" className="p-2 hover:text-[#FF6B00] transition-colors">
          <X size={26} />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center overflow-hidden select-none" onClick={onClose}>
        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); go(-1); }}
            aria-label="Previous image"
            data-testid="lightbox-prev"
            className="absolute left-2 sm:left-6 z-10 p-2 text-white/80 hover:text-[#FF6B00] transition-colors"
          >
            <ChevronLeft size={40} />
          </button>
        )}

        <div
          className="max-w-[92vw] max-h-[78vh] overflow-hidden"
          style={{ cursor: zoom ? 'zoom-out' : 'zoom-in' }}
          onClick={(e) => { e.stopPropagation(); setZoom((z) => !z); }}
          onMouseMove={handleMove}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <img
            src={images[index]}
            alt={alt}
            draggable={false}
            data-testid="lightbox-image"
            className="max-w-[92vw] max-h-[78vh] object-contain transition-transform duration-200 ease-out"
            style={{ transform: zoom ? 'scale(2.4)' : 'scale(1)', transformOrigin: `${origin.x}% ${origin.y}%` }}
          />
        </div>

        {total > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); go(1); }}
            aria-label="Next image"
            data-testid="lightbox-next"
            className="absolute right-2 sm:right-6 z-10 p-2 text-white/80 hover:text-[#FF6B00] transition-colors"
          >
            <ChevronRight size={40} />
          </button>
        )}
      </div>

      {total > 1 && (
        <div className="px-4 py-4 flex gap-2 justify-center overflow-x-auto">
          {images.map((img, i) => (
            <button
              key={img}
              onClick={() => { setZoom(false); onIndexChange(i); }}
              className={`h-14 w-14 flex-shrink-0 overflow-hidden border-2 transition-opacity ${i === index ? 'border-[#FF6B00]' : 'border-transparent opacity-50 hover:opacity-100'}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      <div className="text-center text-neutral-500 text-xs pb-3 hidden sm:block">
        Click image to {zoom ? 'zoom out' : 'zoom in'} &middot; Arrow keys to navigate &middot; Esc to close
      </div>
    </div>
  );
};

export default ImageLightbox;
